/**
 * Historico 12 meses por fornecedor monitorado.
 *
 * 1 mini-grafico de BARRAS HORIZONTAIS por fornecedor (Recharts), com:
 *   - mes no eixo Y (label a esquerda colado na barra)
 *   - barra colorida com valor R$ a direita
 *   - 12 colunas iniciando na primeira competencia disponivel
 *   - meses sem balancete ficam com barra zerada
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Bar, BarChart, Cell, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { api } from "../lib/api";
import { GLASS_CHART_CARD, CHART_AXIS } from "../lib/chartTheme";

interface Props { condominioId: number; onVoltar: () => void }

interface MesItem { periodo: string; label: string; valor: number }
interface FornecedorOut {
  codigo: string;
  nome_exibicao: string;
  categoria: string | null;
  cor_hex: string | null;
  meses: MesItem[];
  total_periodo: number;
}
interface HistoricoResponse {
  condominio_id: number;
  primeira_competencia: string | null;
  n_meses: number;
  fornecedores: FornecedorOut[];
}

function fmtBRL(n: number): string {
  if (n === 0) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function fmtBRLCompact(n: number): string {
  if (n === 0) return "";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });
}

// Label dentro/depois da barra com o valor R$
function LabelValor({ x, y, width, height, value }: {
  x?: number; y?: number; width?: number; height?: number; value?: number;
}) {
  if (value === undefined || value === 0) return null;
  const xText = (x ?? 0) + (width ?? 0) + 6;
  const yText = (y ?? 0) + (height ?? 0) / 2 + 4;
  return (
    <text x={xText} y={yText} className="fill-slate-100" fontSize={11} fontWeight={600}>
      {fmtBRLCompact(value)}
    </text>
  );
}

function GraficoFornecedor({ f }: { f: FornecedorOut }) {
  const cor = f.cor_hex || "#3b82f6";
  const maxVal = Math.max(...f.meses.map((m) => m.valor), 1);
  const dominioMax = maxVal * 1.4;
  const altura = Math.max(220, f.meses.length * 22 + 16);

  return (
    <div className="border rounded-2xl overflow-hidden" style={GLASS_CHART_CARD}>
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-block w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: cor }} />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-100 truncate">{f.nome_exibicao}</div>
            {f.categoria && <div className="text-xs text-slate-400 truncate">{f.categoria}</div>}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-xs text-slate-400">Total no período</div>
          <div className="text-sm font-bold tabular-nums text-slate-100">{fmtBRL(f.total_periodo)}</div>
        </div>
      </div>
      <div className="px-3 py-3" style={{ height: altura }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={f.meses}
            layout="vertical"
            margin={{ top: 4, right: 90, bottom: 4, left: 0 }}
            barCategoryGap="14%"
          >
            <XAxis type="number" hide domain={[0, dominioMax]} />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 11, fill: CHART_AXIS }}
              width={56}
              tickMargin={2}
              interval={0}
              axisLine={false}
              tickLine={false}
            />
            <Bar
              dataKey="valor"
              radius={[0, 4, 4, 0]}
              label={<LabelValor />}
              isAnimationActive={false}
              minPointSize={2}
            >
              {f.meses.map((m, i) => (
                <Cell key={i} fill={m.valor > 0 ? cor : "rgba(255,255,255,0.10)"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function HistoricoFornecedoresPage({ condominioId, onVoltar }: Props) {
  const { data, isLoading, error } = useQuery<HistoricoResponse>({
    queryKey: ["historico-fornecedores", condominioId],
    queryFn: () =>
      api.get<HistoricoResponse>(`/condominios/${condominioId}/historico-fornecedores?n_meses=12`),
  });

  return (
    <div>
      <button
        type="button"
        onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} />
        Voltar
      </button>

      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Histórico por Fornecedor</h2>
        <p className="text-sm text-slate-500 mt-1">
          Gastos mensais com fornecedores recorrentes
          {data?.primeira_competencia ? <> · janela de {data.n_meses} meses a partir de {data.primeira_competencia}</> : null}
        </p>
      </div>

      {isLoading ? (
        <div className="mt-6 flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 size={16} className="animate-spin" /> Carregando histórico...
        </div>
      ) : error ? (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-card text-sm text-red-800">
          Não foi possível carregar o histórico. {error instanceof Error ? error.message : ""}
        </div>
      ) : !data || data.fornecedores.length === 0 ? (
        <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-card text-sm text-slate-600">
          Este condomínio ainda não tem fornecedores cadastrados ou competências processadas.
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
          {data.fornecedores.map((f) => (<GraficoFornecedor key={f.codigo} f={f} />))}
        </div>
      )}
    </div>
  );
}
